import { lazy, Suspense, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { AppShell } from "@/components/layout/AppShell";
import { PageTransition } from "@/components/motion/PageTransition";
import { PersistentChatwoot } from "@/components/layout/PersistentChatwoot";
import { PersistentAffine } from "@/components/layout/PersistentAffine";
import { PersistentRaven } from "@/components/layout/PersistentRaven";
import { PersistentCrm } from "@/components/layout/PersistentCrm";
import { PersistentInsights } from "@/components/layout/PersistentInsights";
import { PersistentGhost } from "@/components/layout/PersistentGhost";
import { PersistentTwentyCrm } from "@/components/layout/PersistentTwentyCrm";
import { PersistentLobeChat } from "@/components/layout/PersistentLobeChat";
import { PersistentUniver } from "@/components/layout/PersistentUniver";
import { useActivityTracker } from "@/lib/activity-tracker/useActivityTracker";
import { usePageContextReporter } from "@/features/ai-context/usePageContextReporter";
import { SCREEN_ROOT_ATTR } from "@/features/ai-context/screenDom";
import { useAppAccess } from "@/hooks/useAppAccess";

const OnboardingWidget = lazy(() => import("@/components/layout/OnboardingWidget"));

export function MainLayout() {
  const location = useLocation();
  useActivityTracker();
  usePageContextReporter();
  const { isInstalled } = useAppAccess();

  // PostHog SPA pageview tracking — capture_pageview:true only fires on initial load,
  // not on React Router client-side navigations (inbox, orders, items, etc.)
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (!posthog.__loaded) return;
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;
    posthog.capture("$pageview", {
      $current_url: window.location.origin + location.pathname + location.search,
    });
  }, [location.pathname, location.search]);
  const isOnPersistentPage =
    location.pathname.startsWith("/inbox") ||
    location.pathname.startsWith("/affine") ||
    location.pathname.startsWith("/raven") ||
    location.pathname.startsWith("/crm") ||
    location.pathname.startsWith("/insights") ||
    location.pathname.startsWith("/blog") ||
    location.pathname.startsWith("/twenty-crm") ||
    location.pathname.startsWith("/ai") ||
    location.pathname.startsWith("/sheets");

  const isOnImmersivePage =
    location.pathname.startsWith("/inbox") ||
    location.pathname === "/affine" ||
    location.pathname.startsWith("/raven") ||
    location.pathname.startsWith("/crm") ||
    location.pathname.startsWith("/insights") ||
    location.pathname === "/storefront/mobile-design" ||
    location.pathname.startsWith("/blog") ||
    location.pathname.startsWith("/twenty-crm") ||
    location.pathname.startsWith("/ai") ||
    location.pathname.startsWith("/sheets");

  return (
    <AppShell>
      {/* Persistent iframes - only mount for installed apps (isInstalled has fail-open) */}
      {isInstalled('chatwoot') && <PersistentChatwoot />}
      {isInstalled('affine') && <PersistentAffine />}
      {isInstalled('raven') && <PersistentRaven />}
      {isInstalled('crm') && <PersistentCrm />}
      {isInstalled('insights') && <PersistentInsights />}
      {isInstalled('ghost') && <PersistentGhost />}
      <PersistentTwentyCrm />
      <PersistentLobeChat />
      <PersistentUniver />

      {/* Onboarding widget - hidden on immersive/full-screen pages */}
      {!isOnImmersivePage && (
        <Suspense fallback={null}>
          <OnboardingWidget />
        </Suspense>
      )}

      {/* Regular page content - hidden when on persistent iframe pages.
          data-ai-screen-root = what the AI's screen text fallback reads (display:contents, no layout box) */}
      {!isOnPersistentPage && (
        <div {...{ [SCREEN_ROOT_ATTR]: "" }} className="contents">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
      )}
    </AppShell>
  );
}

